<template>
  <div>
    <div v-if="order" class="page-content">
      <div class="heading">{{ $t('event_electricity.page_title') }}</div>

      <NewGlassCard class="card-without-title default-mb" title="">
        <div class="header_container">
          <v-btn class="back-button" @click="goOnePageBack">
            <v-icon color="#6E6E6E">mdi-arrow-left</v-icon>
          </v-btn>
          <div class="header_text">
            <div class="header_customer">
              {{ $t('commissioning.details.customer') }}: {{ applicantLabel }}
            </div>
            <div class="header_address">
              {{ $t('commissioning.details.connection_object') }}:
              {{ primaryMeterAddress }}
            </div>
          </div>
          <v-btn
            rounded
            style="color: white; box-shadow: none; pointer-events: none"
            class="ml-2 mr-2"
            :color="statusConfig.color"
          >
            {{ statusConfig.label }}
          </v-btn>
          <v-btn
            v-if="showStartTechnicalInspectionButton"
            class="mr-4"
            color="primary"
            :loading="inspectionStartLoading"
            :disabled="inspectionStartLoading"
            @click="startInspection"
          >
            Prüfung starten
          </v-btn>
        </div>
      </NewGlassCard>
      <v-alert
        v-if="inspectionError && !showInspection"
        type="error"
        text
        class="default-mb"
      >
        Prüfung konnte nicht gestartet werden. Bitte erneut versuchen.
      </v-alert>

      <div :class="$vuetify.breakpoint.xl ? 'event-details-layout' : ''">
        <div :class="$vuetify.breakpoint.xl ? 'event-details-main' : ''">
          <NewGlassCard
            v-if="showInspection"
            class="default-mb"
            title="Anmeldung prüfen"
          >
            <div class="inspection-panel">
              <v-btn
                color="primary"
                :loading="inspectionCompletionLoading"
                :disabled="
                  inspectionCompletionLoading || inspectionRejectionActive
                "
                @click="completeInspection"
              >
                geprüft
              </v-btn>
              <CommissioningInspectionRejection
                :key="order.id"
                :order="order"
                :disabled="inspectionCompletionLoading"
                @active-change="inspectionRejectionActive = $event"
                @update-order="updateLocalOrder"
              />
              <v-alert v-if="inspectionError" type="error" text class="mt-4">
                Prüfung konnte nicht abgeschlossen werden. Bitte erneut
                versuchen.
              </v-alert>
            </div>
          </NewGlassCard>

          <NewGlassCard
            class="default-mb"
            :title="$t('event_electricity.detail_title')"
          >
            <SingleRaw
              first
              class="single-raw"
              :title="$t('commissioning.details.request_date')"
              :editable="false"
            >
              {{ formattedRequestDate }}
            </SingleRaw>
            <SingleRaw
              class="single-raw"
              :title="$t('commissioning.details.case_number')"
              :editable="false"
            >
              {{ order.case_number || payload.case_number || '-' }}
            </SingleRaw>
            <SingleRaw
              class="single-raw"
              :title="$t('commissioning.details.product_group')"
              :editable="false"
            >
              {{ $t('event_electricity.product') }}
            </SingleRaw>
            <SingleRaw
              v-for="(row, index) in detailRows"
              :key="row.key"
              class="single-raw"
              :title="row.title"
              :editable="row.editable"
              :last="index === detailRows.length - 1"
              @edit="onEditRow(row)"
            >
              <div class="multiline">{{ row.value }}</div>
            </SingleRaw>
          </NewGlassCard>
        </div>

        <div :class="$vuetify.breakpoint.xl ? 'event-details-side' : ''">
          <NewGlassCard
            :class="$vuetify.breakpoint.xl ? 'default-mb ml-2' : 'default-mb'"
            class="z-index-0"
            :title="$t('document_history.title')"
          >
            <DocumentHistoryComponent history-type="Commissioning" />
          </NewGlassCard>
          <NewGlassCard
            :class="$vuetify.breakpoint.xl ? 'default-mb ml-2' : 'default-mb'"
            class="z-index-0"
            :title="$t('history.title')"
          >
            <HistoryTable history-type="Commissioning" />
          </NewGlassCard>
        </div>
      </div>
    </div>

    <div v-else-if="isServerError">
      <h1>{{ $t('commissioning.details.loading_error') }}</h1>
      <p>{{ $t('commissioning.details.error_hint') }}</p>
    </div>
  </div>
</template>

<script>
import { NewGlassCard } from '@newnetzportal/netzportal-webapp-storybook-components';
import gql from 'graphql-tag';
import SingleRaw from '@/components/order-details/SingleRaw';
import HistoryTable from '@/components/history-table/HistoryTable';
import DocumentHistoryComponent from '@/components/order-details/DocumentHistoryComponent';
import CommissioningInspectionRejection from '@/components/commissioning-details/CommissioningInspectionRejection.vue';
import { formatCommissioningContactPerson } from '@/helpers/commissioningContactPerson';
import { formatCommissioningBusinessType } from '@/helpers/commissioningBusinessType';
import { getCommissioningStatusDisplay } from '@/helpers/commissioningStatuses';
import { buildJourneyEditUrl } from '@/helpers/commissioningJourneyLinks';
import { formatDate } from '@/helpers/transformToPresentableData';
import {
  getCommissioningOrderById,
  updateCommissioningOrderStatus,
} from '@/services/CommissioningService';
import documentHistoryService from '@/services/DocumentHistoryService';
import { EventBus } from '@/main';

const EVENT_ELECTRICITY_JOURNEY = 'EVENT_ELECTRIC_METER_ORDER';
const INSTALLATION_ORDER_TYPES = ['zaehlereinbau', 'zaehlerwechsel'];
const UNINSTALLATION_ORDER_TYPES = ['zaehlerausbau', 'zaehlerwechsel'];
const TECHNICAL_INSPECTION_ORDER_TYPES = [
  'zaehlereinbau',
  'zaehlerausbau',
  'zaehlerwechsel',
];
const EDITABLE_STATUSES = [
  'ORDER_CREATED',
  'CRITICAL_ORDER_CREATED',
  'ORDER_TECHNICAL_FEASIBILITY',
  'WMSB_ORDER_CREATED',
  'WMSB_IN_REVIEW',
];

function valueOrDash(value) {
  return value === null || value === undefined || value === ''
    ? '-'
    : String(value);
}

function formatAddress(address = {}) {
  const street = [address.street, address.street_number]
    .filter(Boolean)
    .join(' ');
  const city = [address.zip, address.city].filter(Boolean).join(' ');
  const plot = [
    address.district ? `Gemarkung: ${address.district}` : null,
    address.landing ? `Flur: ${address.landing}` : null,
    address.plot_number ? `Flurstück: ${address.plot_number}` : null,
  ].filter(Boolean);

  return [street, city, ...plot].filter(Boolean).join('\n') || '-';
}

function meterAddress(meter = {}) {
  return {
    ...(meter.connection_address || {}),
    ...(meter.connection_address_locator || {}),
  };
}

function formatPosition(position) {
  if (!position) return '-';

  const coordinates = position.coordinates;
  const coordinateText =
    coordinates &&
    [coordinates.lat, coordinates.lng].every(
      (value) => value !== null && value !== undefined && value !== ''
    )
      ? `Koordinaten: ${coordinates.lat}, ${coordinates.lng}`
      : null;

  return (
    [position.description, coordinateText].filter(Boolean).join('\n') || '-'
  );
}

function formatEventDate(value) {
  if (!value) return '-';

  const numericValue = Number(value);
  const date =
    Number.isFinite(numericValue) && String(value).length <= 10
      ? new Date(numericValue * 1000)
      : new Date(value);

  return Number.isNaN(date.getTime())
    ? valueOrDash(value)
    : formatDate(date, 'DD.MM.YYYY');
}

function orderTypeLabel(orderType) {
  const labels = {
    zaehlereinbau: 'Zählereinbau',
    zaehlerausbau: 'Zählerausbau',
    zaehlerwechsel: 'Zählereinbau und Zählerausbau',
  };

  return labels[orderType] || valueOrDash(orderType);
}

function formatMeters(meters, label, getValue) {
  if (meters.length === 1) return getValue(meters[0]);

  return meters
    .map((meter, index) => {
      const meterLabel = label
        ? `${label} Zähler ${index + 1}`
        : `Zähler ${index + 1}`;
      return `${meterLabel}:\n${getValue(meter)}`;
    })
    .join('\n\n');
}

export default {
  name: 'EventElectricityDetailsPage',
  components: {
    NewGlassCard,
    SingleRaw,
    HistoryTable,
    DocumentHistoryComponent,
    CommissioningInspectionRejection,
  },
  props: {
    id: { type: String, required: true },
  },
  data() {
    return {
      order: null,
      isServerError: false,
      inspectionStartLoading: false,
      inspectionCompletionLoading: false,
      inspectionError: false,
      inspectionRejectionActive: false,
    };
  },
  computed: {
    payload() {
      return this.order?.payload || {};
    },
    applicantLabel() {
      return (
        formatCommissioningContactPerson(this.payload.contact_person) || '-'
      );
    },
    primaryMeterAddress() {
      return formatAddress(meterAddress(this.payload)).replace(/\n/g, ' · ');
    },
    formattedRequestDate() {
      return formatEventDate(
        this.payload.request_date || this.order?.created_at
      );
    },
    statusConfig() {
      return getCommissioningStatusDisplay(this.order?.status);
    },
    userId() {
      return this.$store.getters.getUserId;
    },
    supportsTechnicalInspection() {
      return TECHNICAL_INSPECTION_ORDER_TYPES.includes(this.payload.order_type);
    },
    showStartTechnicalInspectionButton() {
      return (
        this.supportsTechnicalInspection &&
        ['ORDER_CREATED', 'CRITICAL_ORDER_CREATED'].includes(this.order?.status)
      );
    },
    showInspection() {
      return (
        this.supportsTechnicalInspection &&
        this.order?.status === 'ORDER_TECHNICAL_FEASIBILITY'
      );
    },
    canEditApplicationDetails() {
      return EDITABLE_STATUSES.includes(this.order?.status);
    },
    hasInstallation() {
      return INSTALLATION_ORDER_TYPES.includes(this.payload.order_type);
    },
    hasUninstallation() {
      return UNINSTALLATION_ORDER_TYPES.includes(this.payload.order_type);
    },
    meters() {
      return [
        {
          id: 'primary',
          connection_address: this.payload.connection_address,
          connection_address_locator: this.payload.connection_address_locator,
          meter_position: this.payload.meter_position,
          simultaneously_required_kw: this.payload.simultaneously_required_kw,
          expected_annual_consumption_kwh:
            this.payload.expected_annual_consumption_kwh,
          counter_id: this.payload.counter_id,
          picture: this.payload.picture,
        },
        ...(Array.isArray(this.payload.additional_meters)
          ? this.payload.additional_meters
          : []),
      ];
    },
    detailRows() {
      const customer = this.payload.contact_person || {};
      const alternate = this.payload.alternate_invoice_recipient;
      const rows = [
        {
          key: 'connection-address',
          title: 'Anschlussadresse',
          value: formatMeters(this.meters, 'Anschlussadresse', (meter) =>
            formatAddress(meterAddress(meter))
          ),
          editable: this.canEditApplicationDetails,
        },
        {
          key: 'meter-position',
          title: 'Position des Zählers',
          value: formatMeters(this.meters, 'Position des Zählers', (meter) =>
            formatPosition(meter.meter_position)
          ),
          editable: this.canEditApplicationDetails,
        },
        {
          key: 'measure-type',
          title: 'Art der Maßnahme',
          value: orderTypeLabel(this.payload.order_type),
          editable: false,
        },
      ];

      if (this.hasInstallation || this.hasUninstallation) {
        const dates = [];
        if (this.hasInstallation) {
          dates.push(
            `Wunscheinbaudatum: ${formatEventDate(
              this.payload.desired_installation_date
            )}`,
            `Beginn der Veranstaltung: ${formatEventDate(
              this.payload.event_start_date
            )}`
          );
        }
        if (this.hasUninstallation) {
          dates.push(
            `Ende der Veranstaltung: ${formatEventDate(
              this.payload.event_end_date
            )}`
          );
        }
        rows.push({
          key: 'event-dates',
          title: 'Termine',
          value: dates.join('\n'),
          editable: this.canEditApplicationDetails,
        });
      }

      if (this.hasInstallation) {
        rows.push({
          key: 'event-power-data',
          title: 'Leistungsaufstellung',
          value: formatMeters(this.meters, '', (meter) =>
            [
              `Gleichzeitig benötigte Leistung: ${valueOrDash(
                meter.simultaneously_required_kw
              )} kW`,
              `Erwarteter Jahresverbrauch: ${valueOrDash(
                meter.expected_annual_consumption_kwh
              )} kWh/Jahr`,
            ].join('\n')
          ),
          editable: this.canEditApplicationDetails,
        });
      }

      if (this.payload.order_type === 'zaehlerausbau') {
        rows.push({
          key: 'counter-data',
          title: 'Zählerdaten',
          value: formatMeters(this.meters, '', (meter) =>
            [
              `Zählernummer: ${valueOrDash(meter.counter_id)}`,
              this.payload.order_type === 'zaehlerausbau'
                ? `Zählerfoto: ${valueOrDash(meter.picture?.picture_name)}`
                : null,
            ]
              .filter(Boolean)
              .join('\n')
          ),
          editable: this.canEditApplicationDetails,
          editTarget: { step: 3, substep: this.hasInstallation ? 2 : 1 },
        });
      }

      rows.push(
        {
          key: 'installation-company',
          title: 'Installationsunternehmen',
          value:
            [
              this.payload.installation_company,
              this.payload.installation_company_email
                ? `E-Mail: ${this.payload.installation_company_email}`
                : null,
            ]
              .filter(Boolean)
              .join('\n') || '-',
          editable: false,
        },
        {
          key: 'responsible-specialist',
          title: 'Verantwortliche Fachkraft',
          value:
            [
              this.payload.responsible_specialist_name,
              this.payload.responsible_specialist_email
                ? `E-Mail: ${this.payload.responsible_specialist_email}`
                : null,
            ]
              .filter(Boolean)
              .join('\n') || '-',
          editable: false,
        },
        {
          key: 'personal-data',
          title: 'Persönliche Daten',
          value: this.applicantLabel,
          editable: this.canEditApplicationDetails,
        }
      );

      const businessType = formatCommissioningBusinessType(
        customer.business_type
      );
      if (businessType) {
        rows.push({
          key: 'customer-business-type',
          title: 'Branche des Kunden',
          value: businessType,
          editable: this.canEditApplicationDetails,
        });
      }

      rows.push(
        {
          key: 'customer-address',
          title: 'Kundenadresse',
          value: formatAddress(customer.address),
          editable: this.canEditApplicationDetails,
        },
        {
          key: 'contact-details',
          title: this.$t(
            'commissioning.details.substep_labels.invoice_recipient_contact_details'
          ),
          value:
            [
              customer.phone_number
                ? `Telefon: ${customer.phone_number}`
                : null,
              customer.email ? `E-Mail: ${customer.email}` : null,
            ]
              .filter(Boolean)
              .join('\n') || '-',
          editable: this.canEditApplicationDetails,
        }
      );

      if (alternate?.enabled !== false && alternate) {
        rows.push({
          key: 'alternate-invoice-recipient',
          title: 'Abweichender Rechnungsempfänger',
          value:
            [
              formatCommissioningContactPerson(alternate),
              formatAddress(alternate.address),
            ]
              .filter((value) => value && value !== '-')
              .join('\n') || '-',
          editable: this.canEditApplicationDetails,
        });
      }

      if (this.payload.order_remark) {
        rows.push({
          key: 'application-remarks',
          title: 'Antragsbemerkungen',
          value: this.payload.order_remark,
          editable: false,
        });
      }

      return rows;
    },
  },
  created() {
    this.loadOrder();
  },
  methods: {
    getChangedValue(valueName, responseObject) {
      let value = null;

      if (responseObject[valueName]) {
        value = JSON.parse(responseObject[valueName]);
      } else if (responseObject.workflow_payload) {
        const workflowPayload = JSON.parse(responseObject.workflow_payload);
        value =
          valueName === 'new_value'
            ? { status: workflowPayload.new_status }
            : { status: workflowPayload.old_status };
      }

      return value;
    },
    parseHistoryStatus(status) {
      if (status === null || status === undefined || status === '') {
        return null;
      }

      if (typeof status !== 'string') {
        return status;
      }

      try {
        return JSON.parse(status);
      } catch (error) {
        return status;
      }
    },
    async emitLiveCommissioningDocumentHistoryEvent(eventData) {
      const wait = (ms) =>
        new Promise((resolve) => {
          setTimeout(resolve, ms);
        });

      for (let attempt = 0; attempt < 10; attempt += 1) {
        try {
          const response =
            await documentHistoryService.getCommissioningDocumentsHistory({
              id: this.id,
              from_index: 0,
              limit: 50,
            });
          const changes =
            response?.commissioningDocumentsHistory?.changes || [];
          const matchingChange = changes.find((change) => {
            try {
              const payload = JSON.parse(change.payload);
              return (
                payload.filename === eventData.filename ||
                payload.filename?.endsWith(`/${eventData.filename}`)
              );
            } catch (error) {
              console.error(
                'Error parsing event electricity document history payload:',
                error
              );
              return false;
            }
          });

          if (matchingChange) {
            EventBus.$emit(
              'update-history-table-commissioning',
              matchingChange
            );
            return;
          }
        } catch (error) {
          console.error(
            'Error fetching event electricity document history after live update:',
            error
          );
        }

        await wait(500);
      }
    },
    async loadOrder() {
      const order = await getCommissioningOrderById(this.id);
      if (!order) {
        this.isServerError = true;
        return;
      }
      this.order = order;
      this.$store.commit('setSelectedCommissioning', order);
      this.isServerError = false;
    },
    async startInspection() {
      if (
        !this.showStartTechnicalInspectionButton ||
        this.inspectionStartLoading
      ) {
        return;
      }

      this.inspectionStartLoading = true;
      this.inspectionError = false;
      try {
        const response = await updateCommissioningOrderStatus(
          {
            id: this.order.id,
            old_value: { status: this.order.status },
            new_value: { status: 'ORDER_TECHNICAL_FEASIBILITY' },
          },
          this.userId
        );
        if (!response || response.error) {
          this.inspectionError = true;
          return;
        }
        this.updateLocalOrder({ status: 'ORDER_TECHNICAL_FEASIBILITY' });
      } catch {
        this.inspectionError = true;
      } finally {
        this.inspectionStartLoading = false;
      }
    },
    async completeInspection() {
      if (
        !this.showInspection ||
        this.inspectionCompletionLoading ||
        this.inspectionRejectionActive
      ) {
        return;
      }

      this.inspectionCompletionLoading = true;
      this.inspectionError = false;
      try {
        const response = await updateCommissioningOrderStatus(
          {
            id: this.order.id,
            old_value: { status: this.order.status },
            new_value: { status: 'TRANSMITTED' },
          },
          this.userId
        );
        if (!response || response.error) {
          this.inspectionError = true;
          return;
        }
        this.updateLocalOrder({ status: 'TRANSMITTED' });
      } catch {
        this.inspectionError = true;
      } finally {
        this.inspectionCompletionLoading = false;
      }
    },
    updateLocalOrder({ status }) {
      this.$store.commit('updateSelectedCommissioningData', { status });
      this.order = { ...this.order, status };
    },
    onEditRow(row) {
      if (!row.editable) return;

      const url = buildJourneyEditUrl(
        EVENT_ELECTRICITY_JOURNEY,
        row.key,
        this.order?.id || this.id,
        row.editTarget
      );
      if (url) window.open(url, '_blank', 'noopener');
    },
    goOnePageBack() {
      this.$router.push(
        this.$store.getters.getPreviousPage || '/baustrom?page=1'
      );
    },
  },
  apollo: {
    $subscribe: {
      commissioningHistory: {
        query: gql`
          subscription onCommissioningHistoryChanged($orderId: String) {
            onCommissioningHistoryChanged(id: $orderId) {
              id
              changed_by
              event_origin
              created
              field
              title
              old_value
              new_value
              workflow_payload
              note
              status
              filename
            }
          }
        `,
        variables() {
          return { orderId: this.id };
        },
        error(error) {
          console.error(
            '[GraphQL] Event electricity commissioning subscription error:',
            error
          );
        },
        result({ data }) {
          const eventData = data.onCommissioningHistoryChanged;
          let oldValueParsed = null;
          let newValueParsed = null;

          try {
            oldValueParsed = this.getChangedValue('old_value', eventData);
            newValueParsed = this.getChangedValue('new_value', eventData);
          } catch (error) {
            console.error(
              'Error parsing history values for event electricity:',
              error
            );
            return;
          }

          const payload = {
            id: eventData.id,
            new_value: newValueParsed,
            old_value: oldValueParsed,
            title:
              eventData.title === '$context.prev.result.payload.title'
                ? null
                : eventData.title,
            note:
              eventData.note === '$context.prev.result.payload.note'
                ? null
                : eventData.note,
            filename: eventData.filename,
            status: this.parseHistoryStatus(eventData.status),
          };

          eventData.payload = JSON.stringify(payload);

          if (newValueParsed) {
            const isOrderPayloadUpdate = [
              'updateCommissioningOrderRequest',
              'updateCommissioningOrderData',
              'updateCommissioningTechnicalInspectionData',
            ].includes(eventData.field);

            if (isOrderPayloadUpdate) {
              this.$store.commit(
                'updateSelectedCommissioningPayloadData',
                newValueParsed
              );
              this.order = {
                ...this.order,
                payload: {
                  ...this.order?.payload,
                  ...newValueParsed,
                },
              };
            } else {
              this.$store.commit(
                'updateSelectedCommissioningData',
                newValueParsed
              );
              this.order = {
                ...this.order,
                ...newValueParsed,
              };
            }
          }

          this.$store.commit(
            'updateLastChangedByInSelectedCommissioning',
            eventData.changed_by
          );
          this.$store.commit(
            'updateLastUpdateInSelectedCommissioning',
            eventData.created
          );
          if (this.order) {
            this.order = {
              ...this.order,
              last_changed_by: eventData.changed_by,
              last_update: eventData.created,
            };
          }

          this.$store.commit('setNotifyEvent', eventData);
          if (payload.filename) {
            this.emitLiveCommissioningDocumentHistoryEvent(eventData);
          } else {
            EventBus.$emit('update-history-table-commissioning', eventData);
          }
        },
      },
    },
  },
};
</script>

<style scoped>
.inspection-panel {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 16px;
  color: #6e6e6e;
}

.heading {
  font-size: 50px;
  margin-bottom: 0;
}

.header_container {
  display: flex;
  align-items: center;
}

.back-button {
  border-radius: 40px;
  background-color: rgba(255, 255, 255, 0.9);
  height: 35px !important;
  width: 90px;
  box-shadow: none;
  margin-right: 30px;
}

.header_text {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.header_customer {
  font-size: 25px;
}

.header_address {
  font-size: 20px;
}

.event-details-layout {
  display: flex;
  align-items: flex-start;
}

.event-details-main {
  flex: 1 1 50%;
  min-width: 0;
}

.event-details-side {
  flex: 1 1 50%;
  min-width: 0;
}

.multiline {
  white-space: pre-line;
}
</style>
